/**
 * ============================================================================
 * ApproveDialog — Phase 6c frontend (CCIE).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: modal that captures the approval signature(s). For bulk CCs
 *          (item_count > 50) we require TWO distinct signature bundles per
 *          CCIE-017; for non-bulk CCs a single signature is accepted. The
 *          server is the source of truth for the threshold — we mirror it
 *          client-side so the UI can show the second input pre-emptively.
 *
 * A11Y: same focus-trap pattern as AcknowledgeDialog. The second signature
 *  field is announced as "Signer B" so SR users understand why two are
 *  needed.
 * ============================================================================ */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import dialogStyles from '../AuthorWorkbench/NewDocumentForm.module.css';
import styles from './ImpactSetReview.module.css';
import { useApproveMutation } from './useApproveMutation';

const BULK_THRESHOLD = 50;

export interface ApproveDialogProps {
  changeId: string;
  itemCount: number;
  onClose: () => void;
  onApproved: () => void;
}

export function ApproveDialog({
  changeId,
  itemCount,
  onClose,
  onApproved,
}: ApproveDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const closeRef = useRef<HTMLButtonElement | null>(null);

  const approveM = useApproveMutation();
  const isBulk = itemCount > BULK_THRESHOLD;

  const [sigA, setSigA] = useState('');
  const [sigB, setSigB] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    closeRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!sigA.trim()) {
      setError(
        t('changeControl.impactSet.errors.signatureRequired', {
          defaultValue: 'A signature bundle id is required.',
        }),
      );
      return;
    }
    if (isBulk) {
      if (!sigB.trim()) {
        setError(
          t('changeControl.impactSet.errors.dualSignRequired', {
            defaultValue: 'Two signature bundle ids are required for bulk approvals.',
          }),
        );
        return;
      }
      if (sigA.trim() === sigB.trim()) {
        setError(
          t('changeControl.impactSet.errors.dualSignDistinct', {
            defaultValue: 'The two signers must be different people.',
          }),
        );
        return;
      }
    }
    try {
      const ids = isBulk ? [sigA.trim(), sigB.trim()] : [sigA.trim()];
      await approveM.mutateAsync({ changeId, signatureBundleIds: ids });
      onApproved();
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : t('changeControl.impactSet.errors.serverFailed', {
              defaultValue: 'Server rejected the action.',
            }),
      );
    }
  }

  return (
    <div
      className={dialogStyles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={dialogStyles.header}>
          <h2 id={titleId} className={dialogStyles.title}>
            {t('changeControl.impactSet.approve.title', {
              defaultValue: 'Approve change control',
            })}
          </h2>
          <p id={descId} className={dialogStyles.desc}>
            {isBulk
              ? t('changeControl.impactSet.approve.subtitleBulk', {
                  defaultValue:
                    'This is a bulk change ({{count}} impacted entities). Approval requires two distinct signature bundles (CCIE-017).',
                  count: itemCount,
                })
              : t('changeControl.impactSet.approve.subtitle', {
                  defaultValue:
                    'A single signature is required. Approval enqueues the post-action workflow.',
                })}
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={onSubmit} noValidate>
          {error && (
            <div role="alert" className={dialogStyles.errorBanner}>
              {error}
            </div>
          )}

          {isBulk && (
            <div className={styles.bulkCallout} role="note">
              {t('changeControl.impactSet.approve.bulkCallout', {
                defaultValue:
                  'Bulk change — {{count}} impacted entities · two signers required.',
                count: itemCount,
              })}
            </div>
          )}

          <div className={dialogStyles.field}>
            <label htmlFor="apr-sig-a" className={dialogStyles.label}>
              {isBulk
                ? t('changeControl.impactSet.approve.sigA', {
                    defaultValue: 'Signature bundle id — Signer A',
                  })
                : t('changeControl.impactSet.approve.signatureLabel', {
                    defaultValue: 'Signature bundle id',
                  })}
              <span className={dialogStyles.required} aria-hidden="true">
                *
              </span>
            </label>
            <input
              id="apr-sig-a"
              className={dialogStyles.input}
              value={sigA}
              onChange={(e) => setSigA(e.target.value)}
              placeholder="sig-…"
              required
            />
          </div>

          {isBulk && (
            <div className={dialogStyles.field}>
              <label htmlFor="apr-sig-b" className={dialogStyles.label}>
                {t('changeControl.impactSet.approve.sigB', {
                  defaultValue: 'Signature bundle id — Signer B',
                })}
                <span className={dialogStyles.required} aria-hidden="true">
                  *
                </span>
              </label>
              <input
                id="apr-sig-b"
                className={dialogStyles.input}
                value={sigB}
                onChange={(e) => setSigB(e.target.value)}
                placeholder="sig-…"
                required
              />
            </div>
          )}

          <footer className={dialogStyles.footer}>
            <Button
              ref={closeRef}
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={approveM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" variant="primary" isLoading={approveM.isPending}>
              {t('changeControl.impactSet.actions.approve', {
                defaultValue: 'Approve',
              })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
